/**
 * Universal Web Audio API Sound Synthesizer for Festival Themes.
 * Supports Halloween, Christmas, New Year, and Valentines with zero external audio files.
 */

let audioCtx = null;

const getAudioContext = () => {
    if (typeof window === 'undefined') return null;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!audioCtx) {
        audioCtx = new AudioCtx();
    }
    if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
    }
    return audioCtx;
};

export const isThemeSoundEnabled = () => {
    if (typeof window === 'undefined') return false;
    try {
        const val = localStorage.getItem('psh_theme_sound');
        if (val !== null) return val === 'true';
        return localStorage.getItem('psh_spooky_sound') === 'true';
    } catch {
        return false;
    }
};

export const setThemeSoundEnabled = (enabled) => {
    if (typeof window === 'undefined') return false;
    try {
        localStorage.setItem('psh_theme_sound', enabled ? 'true' : 'false');
        localStorage.setItem('psh_spooky_sound', enabled ? 'true' : 'false');
    } catch {
        // ignore storage errors
    }
    return enabled;
};

export const playThemeSound = (theme, soundType) => {
    if (!isThemeSoundEnabled()) return;
    try {
        const ctx = getAudioContext();
        if (!ctx) return;
        const now = ctx.currentTime;

        // --- CHRISTMAS SOUNDS ---
        if (theme === 'christmas') {
            switch (soundType) {
                case 'jingle':
                case 'sleigh': {
                    // Sleigh bells cluster: 4 high frequency metallic harmonics
                    const bellFreqs = [2093, 2349, 2793, 3135];
                    bellFreqs.forEach((freq, idx) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(freq, now + idx * 0.03);

                        gain.gain.setValueAtTime(0.001, now + idx * 0.03);
                        gain.gain.linearRampToValueAtTime(0.08 / (idx + 1), now + idx * 0.03 + 0.01);
                        gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.03 + 0.45);

                        osc.connect(gain);
                        gain.connect(ctx.destination);

                        osc.start(now + idx * 0.03);
                        osc.stop(now + idx * 0.03 + 0.5);
                    });
                    break;
                }
                case 'giftPop': {
                    // Gift unwrap pop + magical chime
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(320, now);
                    osc.frequency.exponentialRampToValueAtTime(880, now + 0.15);

                    gain.gain.setValueAtTime(0.18, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

                    osc.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(now);
                    osc.stop(now + 0.25);

                    // Followed by high chime
                    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
                        const o = ctx.createOscillator();
                        const g = ctx.createGain();
                        o.type = 'sine';
                        o.frequency.setValueAtTime(f, now + 0.1 + i * 0.04);
                        g.gain.setValueAtTime(0.06, now + 0.1 + i * 0.04);
                        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.1 + i * 0.04 + 0.6);
                        o.connect(g);
                        g.connect(ctx.destination);
                        o.start(now + 0.1 + i * 0.04);
                        o.stop(now + 0.1 + i * 0.04 + 0.65);
                    });
                    break;
                }
                case 'sparkle': {
                    [1318.5, 1567.98, 1760, 2093].forEach((f, i) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(f, now + i * 0.05);
                        gain.gain.setValueAtTime(0.05, now + i * 0.05);
                        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.05 + 0.4);
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + i * 0.05);
                        osc.stop(now + i * 0.05 + 0.45);
                    });
                    break;
                }
                default:
                    break;
            }
            return;
        }

        // --- NEW YEAR SOUNDS ---
        if (theme === 'newyear') {
            switch (soundType) {
                case 'firework': {
                    // 1. Rising rocket whistle whoosh
                    const whistle = ctx.createOscillator();
                    const wGain = ctx.createGain();
                    whistle.type = 'sine';
                    whistle.frequency.setValueAtTime(400, now);
                    whistle.frequency.exponentialRampToValueAtTime(1400, now + 0.25);
                    wGain.gain.setValueAtTime(0.08, now);
                    wGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
                    whistle.connect(wGain);
                    wGain.connect(ctx.destination);
                    whistle.start(now);
                    whistle.stop(now + 0.26);

                    // 2. Explosion boom crackle at now + 0.28
                    const boomTime = now + 0.28;
                    const bufferSize = ctx.sampleRate * 0.8;
                    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
                    const data = buffer.getChannelData(0);
                    for (let i = 0; i < bufferSize; i++) {
                        data[i] = Math.random() * 2 - 1;
                    }
                    const noise = ctx.createBufferSource();
                    noise.buffer = buffer;

                    const filter = ctx.createBiquadFilter();
                    filter.type = 'lowpass';
                    filter.frequency.setValueAtTime(350, boomTime);
                    filter.frequency.exponentialRampToValueAtTime(45, boomTime + 0.7);

                    const boomGain = ctx.createGain();
                    boomGain.gain.setValueAtTime(0.25, boomTime);
                    boomGain.gain.exponentialRampToValueAtTime(0.001, boomTime + 0.75);

                    noise.connect(filter);
                    filter.connect(boomGain);
                    boomGain.connect(ctx.destination);

                    noise.start(boomTime);
                    noise.stop(boomTime + 0.8);
                    break;
                }
                case 'corkPop':
                case 'balloonPop': {
                    // Crisp pop
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(580, now);
                    osc.frequency.exponentialRampToValueAtTime(120, now + 0.12);
                    gain.gain.setValueAtTime(0.25, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.start(now);
                    osc.stop(now + 0.13);
                    break;
                }
                case 'cheer': {
                    // Party celebration horn fanfare
                    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sawtooth';
                        osc.frequency.setValueAtTime(f, now + i * 0.08);
                        gain.gain.setValueAtTime(0.07, now + i * 0.08);
                        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.08 + 0.4);
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + i * 0.08);
                        osc.stop(now + i * 0.08 + 0.45);
                    });
                    break;
                }
                default:
                    break;
            }
            return;
        }

                // --- DIWALI SOUNDS (GRAND CRACKERS & SACRED HARMONICS) ---
        if (theme === 'diwali') {
            switch (soundType) {
                case 'templeBell':
                case 'bell': {
                    // Resonant sacred bronze temple bell (harmonic rich chime)
                    const bellHarmonics = [587.33, 880.0, 1174.66, 1760.0, 2349.32];
                    bellHarmonics.forEach((freq, idx) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(freq, now);

                        gain.gain.setValueAtTime(0.001, now);
                        gain.gain.linearRampToValueAtTime(0.12 / (idx + 1), now + 0.015);
                        gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.8);

                        osc.connect(gain);
                        gain.connect(ctx.destination);

                        osc.start(now);
                        osc.stop(now + 1.9);
                    });
                    break;
                }
                case 'sitar': {
                    // Sitar plucked note
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sawtooth';
                    osc.frequency.setValueAtTime(293.66, now);
                    osc.frequency.linearRampToValueAtTime(329.63, now + 0.08);
                    osc.frequency.linearRampToValueAtTime(293.66, now + 0.25);

                    const filter = ctx.createBiquadFilter();
                    filter.type = 'bandpass';
                    filter.frequency.setValueAtTime(800, now);
                    filter.Q.setValueAtTime(3, now);

                    gain.gain.setValueAtTime(0.18, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);

                    osc.connect(filter);
                    filter.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(now);
                    osc.stop(now + 0.85);
                    break;
                }
                case 'ladi': {
                    // Rapid firecracker string (10 rapid pops like real Diwali red ladi)
                    for (let i = 0; i < 10; i++) {
                        const popTime = now + i * 0.065 + (Math.random() * 0.02);
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'triangle';
                        osc.frequency.setValueAtTime(800 + Math.random() * 600, popTime);
                        osc.frequency.exponentialRampToValueAtTime(60, popTime + 0.04);

                        gain.gain.setValueAtTime(0.22, popTime);
                        gain.gain.exponentialRampToValueAtTime(0.001, popTime + 0.04);

                        osc.connect(gain);
                        gain.connect(ctx.destination);

                        osc.start(popTime);
                        osc.stop(popTime + 0.05);
                    }
                    break;
                }
                case 'skyShot': {
                    // Rising whistle whoosh + massive colorful burst
                    const whistle = ctx.createOscillator();
                    const wGain = ctx.createGain();
                    whistle.type = 'sawtooth';
                    whistle.frequency.setValueAtTime(350, now);
                    whistle.frequency.exponentialRampToValueAtTime(1800, now + 0.3);
                    wGain.gain.setValueAtTime(0.1, now);
                    wGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
                    whistle.connect(wGain);
                    wGain.connect(ctx.destination);
                    whistle.start(now);
                    whistle.stop(now + 0.31);

                    // Boom crackle
                    const boomTime = now + 0.32;
                    const bufferSize = ctx.sampleRate * 0.9;
                    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
                    const data = buffer.getChannelData(0);
                    for (let i = 0; i < bufferSize; i++) {
                        data[i] = Math.random() * 2 - 1;
                    }
                    const noise = ctx.createBufferSource();
                    noise.buffer = buffer;

                    const filter = ctx.createBiquadFilter();
                    filter.type = 'lowpass';
                    filter.frequency.setValueAtTime(450, boomTime);
                    filter.frequency.exponentialRampToValueAtTime(35, boomTime + 0.8);

                    const bGain = ctx.createGain();
                    bGain.gain.setValueAtTime(0.35, boomTime);
                    bGain.gain.exponentialRampToValueAtTime(0.001, boomTime + 0.85);

                    noise.connect(filter);
                    filter.connect(bGain);
                    bGain.connect(ctx.destination);

                    noise.start(boomTime);
                    noise.stop(boomTime + 0.9);
                    break;
                }
                case 'chakri': {
                    // Whirling spinning sizzle
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sawtooth';
                    osc.frequency.setValueAtTime(440, now);
                    osc.frequency.linearRampToValueAtTime(880, now + 0.4);
                    osc.frequency.linearRampToValueAtTime(440, now + 0.8);

                    gain.gain.setValueAtTime(0.12, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.9);

                    osc.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(now);
                    osc.stop(now + 0.95);
                    break;
                }
                case 'anar':
                case 'cracker': {
                    // Sizzling sparkler fountain crackle
                    for (let i = 0; i < 8; i++) {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'triangle';
                        osc.frequency.setValueAtTime(1400 + Math.random() * 900, now + i * 0.05);
                        gain.gain.setValueAtTime(0.1, now + i * 0.05);
                        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.04);
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + i * 0.05);
                        osc.stop(now + i * 0.05 + 0.045);
                    }
                    break;
                }
                case 'sparkle': {
                    [1174.66, 1318.51, 1567.98, 1760.0, 2093.0].forEach((f, i) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(f, now + i * 0.04);
                        gain.gain.setValueAtTime(0.06, now + i * 0.04);
                        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.04 + 0.6);
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + i * 0.04);
                        osc.stop(now + i * 0.04 + 0.65);
                    });
                    break;
                }
                default:
                    break;
            }
            return;
        }

        // --- VALENTINES SOUNDS ---
        if (theme === 'valentines') {
            switch (soundType) {
                case 'harp':
                case 'loveChime': {
                    // Romantic golden harp chord (F major 9 arpeggio)
                    const notes = [349.23, 440.0, 523.25, 659.25, 698.46, 880.0];
                    notes.forEach((freq, idx) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(freq, now + idx * 0.07);

                        gain.gain.setValueAtTime(0.001, now + idx * 0.07);
                        gain.gain.linearRampToValueAtTime(0.09, now + idx * 0.07 + 0.02);
                        gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.07 + 0.8);

                        osc.connect(gain);
                        gain.connect(ctx.destination);

                        osc.start(now + idx * 0.07);
                        osc.stop(now + idx * 0.07 + 0.85);
                    });
                    break;
                }
                case 'heartbeat': {
                    // Realistic lub-dub heartbeat
                    [0, 0.14].forEach((offset, idx) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(idx === 0 ? 68 : 55, now + offset);
                        gain.gain.setValueAtTime(0.2, now + offset);
                        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.1);
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + offset);
                        osc.stop(now + offset + 0.12);
                    });
                    break;
                }
                case 'arrow': {
                    // Cupid's arrow swoosh
                    const bufferSize = ctx.sampleRate * 0.35;
                    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
                    const data = buffer.getChannelData(0);
                    for (let i = 0; i < bufferSize; i++) {
                        data[i] = Math.random() * 2 - 1;
                    }
                    const noise = ctx.createBufferSource();
                    noise.buffer = buffer;

                    const filter = ctx.createBiquadFilter();
                    filter.type = 'bandpass';
                    filter.frequency.setValueAtTime(1600, now);
                    filter.frequency.exponentialRampToValueAtTime(400, now + 0.32);

                    const gain = ctx.createGain();
                    gain.gain.setValueAtTime(0.01, now);
                    gain.gain.linearRampToValueAtTime(0.14, now + 0.06);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.34);

                    noise.connect(filter);
                    filter.connect(gain);
                    gain.connect(ctx.destination);

                    noise.start(now);
                    noise.stop(now + 0.35);
                    break;
                }
                case 'pop': {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(520, now);
                    osc.frequency.exponentialRampToValueAtTime(140, now + 0.12);
                    gain.gain.setValueAtTime(0.15, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.start(now);
                    osc.stop(now + 0.14);
                    break;
                }
                default:
                    break;
            }
            return;
        }

        // --- HALLOWEEN SOUNDS ---
        if (theme === 'halloween') {
            switch (soundType) {
                case 'bubble':
                case 'cauldronBubble': {
                    [0, 0.08, 0.16].forEach((offset, idx) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(300 + idx * 80, now + offset);
                        osc.frequency.exponentialRampToValueAtTime(550 + idx * 120, now + offset + 0.08);

                        gain.gain.setValueAtTime(0.12, now + offset);
                        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.1);

                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + offset);
                        osc.stop(now + offset + 0.12);
                    });
                    break;
                }
                case 'witchCackle':
                case 'laugh': {
                    [0, 0.09, 0.18, 0.28, 0.40].forEach((offset, idx) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sawtooth';
                        const baseFreq = 580 - idx * 25;
                        osc.frequency.setValueAtTime(baseFreq, now + offset);
                        osc.frequency.linearRampToValueAtTime(baseFreq + 70, now + offset + 0.04);
                        osc.frequency.linearRampToValueAtTime(baseFreq - 30, now + offset + 0.08);

                        gain.gain.setValueAtTime(0.09, now + offset);
                        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.09);

                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + offset);
                        osc.stop(now + offset + 0.1);
                    });
                    break;
                }
                case 'thunder': {
                    const bufferSize = ctx.sampleRate * 0.8;
                    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
                    const data = buffer.getChannelData(0);
                    for (let i = 0; i < bufferSize; i++) {
                        data[i] = Math.random() * 2 - 1;
                    }
                    const noise = ctx.createBufferSource();
                    noise.buffer = buffer;

                    const filter = ctx.createBiquadFilter();
                    filter.type = 'lowpass';
                    filter.frequency.setValueAtTime(220, now);
                    filter.frequency.exponentialRampToValueAtTime(60, now + 0.75);

                    const gain = ctx.createGain();
                    gain.gain.setValueAtTime(0.25, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.78);

                    noise.connect(filter);
                    filter.connect(gain);
                    gain.connect(ctx.destination);
                    noise.start(now);
                    noise.stop(now + 0.8);
                    break;
                }
                case 'spookyChime':
                default: {
                    [440, 523.25, 659.25].forEach((freq, idx) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(freq, now + idx * 0.1);
                        gain.gain.setValueAtTime(0.07, now + idx * 0.1);
                        gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.1 + 0.7);
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + idx * 0.1);
                        osc.stop(now + idx * 0.1 + 0.75);
                    });
                    break;
                }
            }
            return;
        }

        // --- DEFAULT CYBER SOUNDS ---
        if (theme === 'default') {
            switch (soundType) {
                case 'nodeHack': {
                    // Fast digital data chirp
                    [800, 1200, 1600, 2400].forEach((f, i) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'square';
                        osc.frequency.setValueAtTime(f, now + i * 0.03);
                        gain.gain.setValueAtTime(0.05, now + i * 0.03);
                        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.03 + 0.04);
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + i * 0.03);
                        osc.stop(now + i * 0.03 + 0.05);
                    });
                    break;
                }
                case 'cyberUnlock':
                case 'fanfare': {
                    // Futuristic ascending synth chord
                    [440, 554.37, 659.25, 880, 1108.73].forEach((f, i) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.type = 'triangle';
                        osc.frequency.setValueAtTime(f, now + i * 0.06);
                        gain.gain.setValueAtTime(0.08, now + i * 0.06);
                        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.06 + 0.7);
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.start(now + i * 0.06);
                        osc.stop(now + i * 0.06 + 0.75);
                    });
                    break;
                }
                default:
                    break;
            }
            return;
        }

    } catch {
        // audio errors handled silently
    }
};
